/* eslint-disable */
import React, { useState, useCallback, useEffect, useRef, useContext } from "react"
import { useDropzone } from "react-dropzone"
import { SiteContext } from "providers/site"
import { SITE_STATUS_CONFIRMED_ID } from "constants/siteStatuses"
import { requestDeleteSiteFiles } from "api/site"

const FILE_LIMIT_MB = 50
const MAX_FILES = 10
const BYTES_PER_MB = 1024 * 1024

const fileSizeLimits = {
  ".zip": 50 * BYTES_PER_MB,
  ".shp": 50 * BYTES_PER_MB,
  ".geojson": 50 * BYTES_PER_MB,
  ".kml": 50 * BYTES_PER_MB,
  ".pdf": 50 * BYTES_PER_MB,
  ".doc": 50 * BYTES_PER_MB,
  ".docx": 50 * BYTES_PER_MB,
  ".xls": 50 * BYTES_PER_MB,
  ".xlsx": 50 * BYTES_PER_MB,
  ".csv": 50 * BYTES_PER_MB,
  ".jpeg": 50 * BYTES_PER_MB,
  ".jpg": 50 * BYTES_PER_MB,
  ".png": 50 * BYTES_PER_MB,
  ".bmp": 50 * BYTES_PER_MB,
  ".tiff": 50 * BYTES_PER_MB,
  ".webp": 50 * BYTES_PER_MB,
}

const validateFileSize = (file) => {
  const ext = file.name.slice(((file.name.lastIndexOf(".") - 1) >>> 0) + 2).toLowerCase()
  const limit = fileSizeLimits[`.${ext}`]
  if (!limit || file.size > limit) {
    return `This ${ext} file is too large`
  }
  return null
}

function normalizeIncomingFile(f) {
  return {
    ...f,
    id: f.id || f.fileUrl || f.originalFilename || f.name,
    name: f.originalFilename || f.name,
  }
}

export default function Uploader({
  params,
  siteFiles = [],
  disabled,
  uploadFn,
  deleteFn,
  fetchFilesFn,
  fileType = "site", // 'site' for about page files, 'species' for species files
  allowDownloadsWhenConfirmed = false, // For Criterion E files, downloads should be available even when confirmed
}) {
  const { site } = useContext(SiteContext)
  const isConfirmed = site?.SiteStatusId === SITE_STATUS_CONFIRMED_ID
  const showDownloads = !isConfirmed || allowDownloadsWhenConfirmed
  const [files, setFiles] = useState([])
  const [uploadProgress, setUploadProgress] = useState({}) // { [name]: number }
  const [uploadStatus, setUploadStatus] = useState({}) // { [name]: 'uploading' | 'success' | 'error' }
  const [totalSize, setTotalSize] = useState(0)
  const simTimersRef = useRef({}) // { [name]: intervalId }

  // Initialize files from siteFiles prop only once
  useEffect(() => {
    if (siteFiles && siteFiles.length > 0) {
      const formatted = siteFiles.map(normalizeIncomingFile)
      setFiles(formatted)
    }
  }, []) // Only run once on mount

  useEffect(() => {
    let cancelled = false
    async function load() {
      if (!fetchFilesFn || !params?.id) return
      try {
        const res = await fetchFilesFn(params.id, params.siteHistoryId)
        const fetched = (res?.files || res || []).map(normalizeIncomingFile)
        if (!cancelled) {
          setFiles(Array.isArray(fetched) ? fetched : [])
        }
      } catch (error) {
        console.warn("[Uploader][FETCH:failed]", error)
        if (!cancelled) {
          setFiles([])
        }
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [fetchFilesFn, params?.id, params?.siteHistoryId])

  const startSimProgress = useCallback((name) => {
    if (simTimersRef.current[name]) clearInterval(simTimersRef.current[name])
    simTimersRef.current[name] = setInterval(() => {
      setUploadProgress((prev) => {
        const cur = prev[name] ?? 0
        const increment = cur < 20 ? 15 : cur < 60 ? 8 : cur < 85 ? 4 : 2
        const next = Math.min(cur + increment, 90)
        return { ...prev, [name]: next }
      })
    }, 200)
  }, [])

  const clearSimProgress = useCallback((name) => {
    const id = simTimersRef.current[name]
    if (id) {
      clearInterval(id)
      delete simTimersRef.current[name]
    }
  }, [])

  const uploadSingleFile = useCallback(
    async (file) => {
      const name = file.name
      setUploadProgress((p) => ({ ...p, [name]: 0 }))
      setUploadStatus((s) => ({ ...s, [name]: 'uploading' }))

      const formData = new FormData()
      formData.append("files", file, file.name)

      try {
        startSimProgress(name)

        const options = {
          onProgress: (loaded, total) => {
            if (!total) return
            const pct = Math.max(0, Math.min(100, Math.round((loaded / total) * 100)))
            setUploadProgress((p) => ({ ...p, [name]: pct }))
            if (pct > 0) {
              clearSimProgress(name)
            }
          },
          file,
        }

        await uploadFn(params.id, params.siteHistoryId, formData, options)

        clearSimProgress(name)
        setUploadProgress((p) => ({ ...p, [name]: 100 }))
        setUploadStatus((s) => ({ ...s, [name]: "success" }))

        // Clear success status after 2 seconds
        setTimeout(() => {
          setUploadStatus((s) => {
            const newStatus = { ...s }
            delete newStatus[name]
            return newStatus
          })
        }, 2000)

      } catch (e) {
        clearSimProgress(name)
        setUploadProgress((p) => ({ ...p, [name]: 0 }))
        setUploadStatus((s) => ({ ...s, [name]: "error" }))
        
        // Clear error status after 3 seconds
        setTimeout(() => {
          setUploadStatus((s) => {
            const newStatus = { ...s }
            delete newStatus[name]
            return newStatus
          })
        }, 3000)
        
        alert(`Upload failed for ${name}. Please try again.`)
      }
    },
    [uploadFn, params?.id, params?.siteHistoryId, startSimProgress, clearSimProgress, fileType]
  )

  const handleFilesUploadsForSite = useCallback(
    async (acceptedFiles) => {
      for (const f of acceptedFiles) {
        // eslint-disable-next-line no-await-in-loop
        await uploadSingleFile(f)
      }
      if (fetchFilesFn && params?.id) {
        try {
          const fresh = await fetchFilesFn(params.id, params.siteHistoryId)
          const fetched = (fresh?.files || fresh || []).map(normalizeIncomingFile)
          setFiles(Array.isArray(fetched) ? fetched : [])
        } catch (error) {
          console.error("[Uploader][UPLOAD:refresh-failed]", error)
        }
      }
    },
    [uploadSingleFile, fetchFilesFn, params?.id, params?.siteHistoryId]
  )

  const onDrop = useCallback(
    async (acceptedFiles) => {
      if (disabled) return

      if (files.length + acceptedFiles.length > MAX_FILES) {
        alert(`You can only upload up to ${MAX_FILES} files.`)
        return
      }

      let batchSize = 0
      for (const f of acceptedFiles) {
        batchSize += f.size
        const err = validateFileSize(f)
        if (err) {
          alert(err)
          return
        }
      }

      if (totalSize + batchSize > FILE_LIMIT_MB * BYTES_PER_MB) {
        alert(`The total size of all files should not exceed ${FILE_LIMIT_MB}MB.`)
        return
      }

      const newFiles = acceptedFiles.map((file) => ({
        id: `temp-${file.name}-${Date.now()}`,
        name: file.name,
        fileUrl: null,
        size: file.size,
      }))
      setFiles((prev) => [...prev, ...newFiles])

      setTotalSize((s) => s + batchSize)
      await handleFilesUploadsForSite(acceptedFiles)
    },
    [disabled, files.length, totalSize, handleFilesUploadsForSite]
  )

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      "application/zip": [".zip"],
      "application/x-shapefile": [".shp"],
      "application/geo+json": [".geojson"],
      "application/vnd.google-earth.kml+xml": [".kml"],
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
      "text/csv": [".csv"],
      "image/jpeg": [".jpeg", ".jpg"],
      "image/png": [".png"],
      "image/bmp": [".bmp"],
      "image/tiff": [".tiff"],
      "image/webp": [".webp"],
    },
    disabled: disabled,
  })

  const handleDelete = useCallback(
    async (fileName, isAlreadyUploaded = false, fileIdOrUrl) => {
      if (isAlreadyUploaded) {
        const deleteFunction = deleteFn || requestDeleteSiteFiles
        try {
          await deleteFunction(params.id, params.siteHistoryId, fileIdOrUrl)
          if (fetchFilesFn && params?.id) {
            try {
              const fresh = await fetchFilesFn(params.id, params.siteHistoryId)
              const fetched = (fresh?.files || fresh || []).map(normalizeIncomingFile)
              setFiles(Array.isArray(fetched) ? fetched : [])
            } catch (error) {
              console.warn("[Uploader][DELETE:refresh-failed]", error)
              setFiles((prev) => prev.filter((file) => file.name !== fileName))
            }
          } else {
            setFiles((prev) => prev.filter((file) => file.name !== fileName))
          }
        } catch (error) {
          console.error("[Uploader][DELETE:failed]", error)
          return
        }
      } else {
        setFiles((prev) => prev.filter((file) => file.name !== fileName))
      }
    },
    [deleteFn, fetchFilesFn, params?.id, params?.siteHistoryId]
  )

  useEffect(() => {
    return () => {
      Object.values(simTimersRef.current).forEach((id) => clearInterval(id))
      simTimersRef.current = {}
    }
  }, [])

  return (
    <div style={{ width: "100%" }}>
      <style>
        {`
          @keyframes shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
        `}
      </style>
      <div
        {...getRootProps({ disabled })}
        style={{
          border: "2px dashed #d1d5db",
          borderRadius: "8px",
          height: "100px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          cursor: disabled ? "not-allowed" : "pointer",
          backgroundColor: disabled ? "#f9fafb" : "#fafafa",
          transition: "all 200ms ease-out",
          color: disabled ? "#9ca3af" : "#6b7280",
          fontWeight: "500",
          fontSize: "14px",
          lineHeight: "20px",
          padding: "16px",
          textAlign: "center",
        }}
        onMouseEnter={(e) => {
          if (!disabled) {
            e.target.style.borderColor = "#3b82f6"
            e.target.style.backgroundColor = "#f0f9ff"
            e.target.style.color = "#1e40af"
          }
        }}
        onMouseLeave={(e) => {
          if (!disabled) {
            e.target.style.borderColor = "#d1d5db"
            e.target.style.backgroundColor = "#fafafa"
            e.target.style.color = "#6b7280"
          }
        }}
      >
        {!disabled && <input {...getInputProps()} />}
        {disabled ? "File uploading is disabled" : "Drag & Drop your files or click to select files"}
      </div>

      <ul
        style={{
          listStyle: "none",
          padding: 0,
          margin: "16px 0 0 0",
          width: "100%",
        }}
      >
        {files.map((file, idx) => {
          const isUploaded = !!file.fileUrl
          const key = file.id || file.fileUrl || `${file.name}-${idx}`
          const progress = uploadProgress[file.name]
          const status = uploadStatus[file.name]
          const showProgress = typeof progress === "number" && progress < 100
          const isUploading =
            status === "uploading" || (typeof progress === "number" && progress > 0 && progress < 100)
          const isSuccess = status === "success"
          const isError = status === "error"

          return (
            <li
              key={key}
              style={{
                margin: "0 0 12px 0",
                position: "relative",
                padding: "14px 16px",
                backgroundColor: isError
                  ? "#fef2f2"
                  : isSuccess
                  ? "#f0fdf4"
                  : isUploading
                  ? "#f0f9ff"
                  : isUploaded
                  ? "#f0fdf4"
                  : "#fafafa",
                borderRadius: "8px",
                border: isError
                  ? "1px solid #fecaca"
                  : isSuccess
                  ? "1px solid #dcfce7"
                  : isUploading
                  ? "1px solid #dbeafe"
                  : isUploaded
                  ? "1px solid #dcfce7"
                  : "1px solid #f3f4f6",
                transition: "all 200ms ease-out",
                boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  width: "100%",
                  flexWrap: "wrap",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    flex: "1 1 auto",
                    minWidth: "200px",
                  }}
                >
                  <span
                    style={{
                      fontWeight: isUploading ? "500" : "400",
                      fontSize: "14px",
                      lineHeight: "20px",
                      color: isError
                        ? "#dc2626"
                        : isSuccess
                        ? "#059669"
                        : isUploading
                        ? "#1f2937"
                        : "#374151",
                      wordBreak: "break-word",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {file.name}
                  </span>
                  {isSuccess && <span style={{ color: "#059669", fontSize: "16px", lineHeight: "1", flexShrink: 0 }}>✓</span>}
                  {isError && <span style={{ color: "#dc2626", fontSize: "16px", lineHeight: "1", flexShrink: 0 }}>✗</span>}
                  {isUploading && <span style={{ color: "#3b82f6", fontSize: "16px", lineHeight: "1", flexShrink: 0 }}>⟳</span>}
                </div>

                {(!isUploaded || showProgress) && (
                  <div
                    style={{
                      height: "8px",
                      minWidth: "120px",
                      maxWidth: "200px",
                      flex: "1 1 auto",
                      border: "1px solid #d1d5db",
                      position: "relative",
                      borderRadius: "4px",
                      overflow: "hidden",
                      backgroundColor: "#f9fafb",
                    }}
                  >
                    <div
                      style={{
                        width: `${typeof progress === "number" ? progress : 0}%`,
                        height: "100%",
                        backgroundColor: isError
                          ? "#ef4444"
                          : isSuccess
                          ? "#10b981"
                          : isUploading
                          ? "#3b82f6"
                          : "#22c55e",
                        transition: "width 200ms ease-out, background-color 200ms ease-out",
                        borderRadius: "inherit",
                        position: "relative",
                      }}
                    >
                      {isUploading && (
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
                            animation: "shimmer 1.5s infinite",
                          }}
                        />
                      )}
                    </div>
                  </div>
                )}

                {showProgress && (
                  <span
                    style={{
                      minWidth: "48px",
                      fontSize: "12px",
                      lineHeight: "16px",
                      fontWeight: "500",
                      color: isError
                        ? "#ef4444"
                        : isSuccess
                        ? "#10b981"
                        : isUploading
                        ? "#3b82f6"
                        : "#22c55e",
                      textAlign: "right",
                      flexShrink: 0,
                    }}
                  >
                    {Math.round(progress)}%
                  </span>
                )}

                {isUploaded && showDownloads && (
                  <a
                    href={file.fileUrl}
                    download={file.name || undefined}
                    style={{
                      marginLeft: "auto",
                      background: "#10b981",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      width: "36px",
                      height: "36px",
                      cursor: "pointer",
                      textAlign: "center",
                      lineHeight: "36px",
                      textDecoration: "none",
                      fontSize: "16px",
                      fontWeight: "500",
                      transition: "all 200ms ease-out",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = "#059669"
                      e.target.style.transform = "scale(1.05)"
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = "#10b981"
                      e.target.style.transform = "scale(1)"
                    }}
                    aria-label={`Download ${file.name}`}
                    title={`Download ${file.name}`}
                  >
                    ↓
                  </a>
                )}

                {!disabled && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      const idOrUrl = file.id || file.fileUrl || file.originalFilename
                      handleDelete(file.name, isUploaded, idOrUrl)
                    }}
                    style={{
                      background: "#ef4444",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      width: "36px",
                      height: "36px",
                      cursor: "pointer",
                      fontSize: "18px",
                      fontWeight: "500",
                      transition: "all 200ms ease-out",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      marginLeft: "8px",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = "#dc2626"
                      e.target.style.transform = "scale(1.05)"
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = "#ef4444"
                      e.target.style.transform = "scale(1)"
                    }}
                    aria-label={`Delete ${file.name}`}
                    title={`Delete ${file.name}`}
                  >
                    ×
                  </button>
                )}
              </div>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
